import moment from 'moment-timezone'
import numeral from 'numeral'
import Vue from 'vue'
import * as SystemOfUnits from '@/lib/system-of-units'
import UTCDateTime from '@/lib/utc-datetime'
import GnssSource from '@/lib/gnss-source'

require('moment-duration-format')

const NoValue = '--'

export function aidAreaText(areaAsSqMeter, showWithSquareMeter = false) {
  const absValue = Math.abs(areaAsSqMeter)
  return SystemOfUnits.getAreaTextFrom(absValue, showWithSquareMeter)
}

export function aidDistanceText(distanceAsMeter) {
  return SystemOfUnits.getDistanceTextFrom(distanceAsMeter)
}

export function aidSpeedText(speedMeterPerSecond) {
  return SystemOfUnits.getSpeedTextFrom(speedMeterPerSecond)
}

export function aidToYearMonthDay(utcString) {
  return UTCDateTime.toYearMonthDay(utcString)
}

export function aidToYearMonthDayHourMin(utcString) {
  return UTCDateTime.toYearMonthDayHourMin(utcString)
}

export function aidToHourMin(utcString) {
  return UTCDateTime.toHourMin(utcString)
}

export function aidToUtcObject(utcString) {
  return UTCDateTime.toUtcMoment(utcString)
}

export function aidToUtcStringFromMoment(momentObject) {
  return UTCDateTime.utcStringFromMomentObject(momentObject)
}

// TaskRecordの作業面積を平方メートルで返す
// C# DocForm TaskRecordRecyclerViewAdapter #OnBindViewHolderから抜粋
export function aidTaskRecordArea(taskRecord) {
  return taskRecord.workedAreaOnEnded - taskRecord.workedAreaOnStarted
}

export function aidTaskRecrodAreaFormatted(taskRecord) {
  const area = aidTaskRecordArea(taskRecord)
  return SystemOfUnits.getAreaTextFrom(area)
}

// TaskRecordの走行距離をメートルで返す
// C# DocForm TaskRecordRecyclerViewAdapter #OnBindViewHolderから抜粋
export function aidTaskRecordDistance(taskRecord) {
  return taskRecord.distanceOnEnded - taskRecord.distanceOnStarted
}

// TaskRecordの作業時間をMoment.jsのDurationオブジェクトで返す
export function aidTaskRecordWorkingTimeDuration(taskRecord) {
  let endTime = aidToUtcObject(taskRecord.endTime)
  let startTime = aidToUtcObject(taskRecord.startTime)
  let duration = moment.duration(endTime.diff(startTime))
  // console.log('endTime:' + taskRecord.endTime, 'startTime:' + taskRecord.startTime, 'duration:' + duration)
  return duration
}

// TaskRecordの作業時間を'hh:mm:ss'の文字列にフォーマットして返す
export function aidTaskRecordWorkingTimeDurationFormatted(taskRecord) {
  let endTime = aidToUtcObject(taskRecord.endTime)
  let startTime = aidToUtcObject(taskRecord.startTime)
  const duration = moment.duration(endTime.diff(startTime, 'seconds'), 'seconds')
  return duration.format('hh:mm:ss', { trim: false })
}

// C# DocForm TaskRecordRecyclerViewAdapter #OnBindViewHolderから抜粋
export function aidTaskRecordEfficiencyDescription(taskRecord) {
  const area = aidTaskRecordArea(taskRecord)
  const farea = Math.abs(taskRecord.fieldArea)
  let areaPercentageText = NoValue
  if (farea > 0) {
    const percentage = (area / farea) * 100
    const fieldArea = SystemOfUnits.getAreaTextFrom(farea)
    // "{fieldArea}中 {percentage}%"
    areaPercentageText = Vue.prototype.$tt('customDocFormTaskRecordWorkedAreaPercentageFormatBase', {
      percentage: numeral(percentage).format('0'),
      fieldArea: fieldArea,
    })
  }

  let efficiencyText = NoValue
  const totalHours = aidTaskRecordWorkingTimeDuration(taskRecord).asHours()
  if (totalHours > 0) {
    const workedAreaPerHour = area / totalHours
    efficiencyText = SystemOfUnits.getAreaTextFrom(workedAreaPerHour)
  }

  // {areaPercentageText}, {efficiencyText}/h
  const text = Vue.prototype.$tt('customDocFormTaskRecordWorkedAreaInfoFormatBase', {
    areaPercentageText: areaPercentageText,
    efficiencyText: efficiencyText,
  })
  return text
}

export function aidTaskRecordAverageSpeed(taskRecord) {
  const distance = aidTaskRecordDistance(taskRecord)
  const duration = aidTaskRecordWorkingTimeDuration(taskRecord)
  const totalSeconds = duration.asSeconds()
  if (totalSeconds > 0) {
    const averageSpeed = distance / totalSeconds
    return averageSpeed
  }
  return 0
}

export function aidTaskRecordDistanceDescription(taskRecord) {
  const distance = aidTaskRecordDistance(taskRecord)
  const averageSpeed = aidTaskRecordAverageSpeed(taskRecord)
  // const duration = aidTaskRecordWorkingTimeDuration(taskRecord)
  // const totalSeconds = duration.asSeconds()
  // if (0 < totalSeconds) {
  if (averageSpeed > 0) {
    // const averageSpeed = distance / totalSeconds
    const speedText = aidSpeedText(averageSpeed)
    const distanceText = aidDistanceText(distance)

    // "{distance} (平均 {speed})"
    const averageSpeedText = Vue.prototype.$tt('customDocFormTaskRecordDistanceOfFormatBase', {
      distance: distanceText,
      speed: speedText,
    })
    return averageSpeedText
  }

  return NoValue
}

export function aidTaskRecordWorkWidth(taskRecord) {
  const started = taskRecord.workWidthOnStarted
  const ended = taskRecord.workWidthOnEnded
  const maxValue = Math.max(started, ended)
  return maxValue
}

export function aidFormatPrice(price, currency) {
  const number = numeral(price)
  switch (currency) {
    case 'jpy':
      return '¥' + number.format('0,0')
    case 'usd':
      return '$' + number.format('0,0.00')
    default:
      return number.format('0,0.00')
  }
}

export function aidWindSpeedText(speedMeterPerSecond) {
  return SystemOfUnits.getWindSpeedTextFrom(speedMeterPerSecond)
}

export function aidTemperature(celsiusTemperature, digits = 1) {
  return SystemOfUnits.getTemperatureTextFrom(celsiusTemperature, digits)
}

export function aidPercent(percent) {
  return SystemOfUnits.getPercent(percent)
}

export function aidMilliMetres(milliMetres) {
  return SystemOfUnits.getMilliMetres(milliMetres)
}

export function aidGnssSourceText(taskRecord) {
  return GnssSource.getLocalizedName(taskRecord)
}
